import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DateRange } from '../../../../lib/types/DateRange'
import { useSearchParams } from 'react-router-dom'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import {
  Info,
  Table,
  MonthPicker,
  RadioInput,
  SelectInput,
} from '@demandlocal/components'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { useGetDealershipConversionGoalsByDealershipID } from '../../../../api/dealership/audiences/useGetConversionGoalsByDealershipID'
import { DisplayCreative } from '../../../../lib/types/DisplayCreativeMetrics'
import { useGetCreativesConversions } from '../../../../api/dealership/performance/conversions/useGetCreativesConversions'
import { useVehiclesListView } from '../../../../store/zustand/vehiclesListStore'
import { ReactComponent as IconChangeViewCard } from '../../../../images/icons/icon_change_view_card.svg'
import { ReactComponent as IconChangeViewList } from '../../../../images/icons/icon_change_view_list.svg'
import Grid from '../../../../components/Grid/Grid'
import GridItemCreative from '../../../../components/Grid/Items/GridItemCreative'
import FiltersContext from '../../../../components/Filters/FiltersContext'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import { useGetTagCodes } from '../../../../api/utils/useGetTagCodes'
import SortingController from '../../../../components/Sorting/SortingController'
import { motion } from 'framer-motion'
import useGetCreativeVendors from '../../../../api/dealership/utils/useGetCreativeVendors'

const creativesColumnHelper = createColumnHelper<
  DisplayCreative & Record<string, number>
>()

const AudienceTemplate = ({
  children,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { dateRange, setDateRange } = useDateRange()
  const dealership = useDealership()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  return (
    <div className={'flex flex-1 flex-col h-full gap-3'} id={'content'}>
      <PageHeader
        title={`${dealership?.dealership} - Creative Performance`}
        rightSlot={() => (
          <div className={'flex flex-row gap-2 items-center'}>
            <MonthPicker
              maxDate={dayjs().toDate()}
              defaultDate={dayjs(dateRange.dateRange.startDate).toDate()}
              onChange={(date) => {
                const dateRange = {
                  dateRange: {
                    startDate: dayjs(date).startOf('month').format('YYYY-MM-DD'),
                    endDate: dayjs(date).endOf('month').format('YYYY-MM-DD'),
                  },
                }
                onDateRangeChange(dateRange)
              }}
            />
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        {children}
      </div>
    </div>
  )
}

const CreativeReport = () => {
  const [queryParams, setQueryParams] = useSearchParams()
  const { view, setView } = useVehiclesListView()
  const { dateRange, setDateRange } = useDateRange()
  const [gridFilters, setgridFilters] = useState({})
  const { data: tag_names } = useGetDealershipConversionGoalsByDealershipID()
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const sortingPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [sorting, setSorting] = useState<Record<string, unknown>>({ clicks: 'desc' })
  const [filteredTotal, setFilteredTotal] = useState<number | undefined>(undefined)

  const [filters, setFilters] = useState({
    ...gridFilters,
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })
  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const { data: { data: tagCodes } = { data: [] } } = useGetTagCodes({ pageSize: 999 })

  const defaultConversions = useMemo(() => {
    return {
      leads: tagCodes?.find((d) => d.id === 1),
      click_to_call: tagCodes?.find((d) => d.id === 11),
      click_to_chat: tagCodes?.find((d) => d.id === 28),
    }
  }, [tagCodes])

  const stableTagNames = useMemo(() => tag_names, [tag_names])

  const creativesColumns: any[] = useMemo(() => {
    const filteredTags = (stableTagNames || []).filter(
      (tag) =>
        !Object.values(defaultConversions || {})
          .map((c) => c?.id)
          .includes(tag.id) && tag.id !== 4,
    )
    return [
      creativesColumnHelper.accessor('creative_name', {
        header: 'Creative',
        cell: (info) => info.getValue(),
      }),
      creativesColumnHelper.accessor('creative_size', {
        header: 'Size',
        cell: (info) => info.getValue(),
      }),
      creativesColumnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      creativesColumnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      creativesColumnHelper.accessor('All Page Views', {
        header: 'All Page Views',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      creativesColumnHelper.accessor('total_conversion', {
        header: () => {
          return (
            <div className="flex flex-row gap-2 items-center">
              <span>Conversions</span>
              <Info
                message={`Sum of ${Object.values(defaultConversions)
                  ?.map((tag) => tag?.tag_name)
                  ?.join(', ')}`}
              />
            </div>
          )
        },
        cell: ({ row }) => {
          return Object.values(defaultConversions).reduce(
            (acc, cur) => acc + Number(row.original[cur?.tag_name as string] || 0),
            0,
          )
        },
      }),
      ...(filteredTags?.length > 0
        ? filteredTags.map((tag) => {
            return creativesColumnHelper.display({
              id: tag.tag_name,
              header: () => {
                return (
                  <div className="flex flex-row gap-1 items-center">{tag.tag_name}</div>
                )
              },
              cell: ({ row }) => formatNumber(Number(row.original[tag.tag_name]) || 0),
            })
          })
        : []),
    ]
  }, [defaultConversions, stableTagNames])

  useEffect(() => {
    setFilters((prevFilters) => ({ ...prevFilters, ...gridFilters }))
  }, [gridFilters])

  const { data: creativeVendors } = useGetCreativeVendors({
    filters: {
      ...filters.dateRange,
    },
  })

  const platformOptions = useMemo(() => {
    return !creativeVendors?.length
      ? []
      : creativeVendors?.map((data) => ({ label: data.vendor, value: data.id }))
  }, [creativeVendors])

  const displayFilterCount = useMemo(() => {
    return Boolean(
      filteredTotal &&
        filteredTotal > 0 &&
        Object.values(filters).filter((f) => !!f)?.length > 0,
    )
  }, [filteredTotal, filters])

  return (
    <AudienceTemplate filters={filters} setFilters={setFilters}>
      <div className="py-5 flex flex-col gap-6 w-full font-Spartan">
        <div className="flex flex-col gap-3">
          <span className={'font-bold text-primary-xiketic text-md'}>
            Top Converting Creatives
          </span>
          <div className="flex flex-row justify-between items-center">
            <OutPortal node={sortingPortalNode} />

            <div className="flex flex-row gap-2 items-center">
              {displayFilterCount && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  key={'filters-total'}
                  className={
                    'h-5 bg-primary-persian-blue text-center flex items-center text-white p-1.5 rounded-md text-xs font-bold'
                  }>
                  <span>{filteredTotal} Creatives</span>
                </motion.div>
              )}
              <div>
                <OutPortal node={filtersPortalNode} />
              </div>
              <div className={'flex items-center gap-3'}>
                {view === 'grid' && (
                  <div onClick={() => setView(view === 'grid' ? 'table' : 'grid')}>
                    <IconChangeViewList
                      className={
                        'cursor-pointer stroke-primary-xiketic fill-primary-xiketic'
                      }
                    />
                  </div>
                )}
                {view === 'table' && (
                  <div onClick={() => setView(view === 'table' ? 'grid' : 'table')}>
                    <IconChangeViewCard
                      className={'cursor-pointer stroke-primary-xiketic'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          {view === 'grid' && (
            <SortingController
              sorting={sorting}
              portalNode={sortingPortalNode}
              setSorting={setSorting}
              choices={[
                { label: 'Impressions', key: 'impressions' },
                { label: 'Clicks', key: 'clicks' },
                ...(tag_names?.length && tag_names?.length > 0
                  ? tag_names?.map((tag) => ({
                      label: tag.tag_name,
                      key: tag.tag_name,
                    }))
                  : []),
              ]}
            />
          )}
          <FiltersContext portalNode={filtersPortalNode} setFilters={setgridFilters}>
            <RadioInput
              name={'creative_type'}
              options={[
                { label: 'Display', value: 'display' },
                { label: 'Video', value: 'video' },
              ]}
              label={'Creative Type'}
            />
            <SelectInput
              name={'vendor_id'}
              label={'Platform'}
              options={platformOptions}
            />
          </FiltersContext>

          {view === 'grid' && (
            <Grid
              fetchFn={useGetCreativesConversions}
              gridComponent={GridItemCreative}
              filters={{ ...filters.dateRange, ...filters }}
              sorting={sorting as Record<string, string | undefined>}
              setTotalRows={(v) => setFilteredTotal(v)}
            />
          )}
          {view === 'table' && (
            <Table
              emptyLabel={'No creatives found'}
              columns={creativesColumns}
              filters={{ ...filters.dateRange, ...filters }}
              fetchFn={useGetCreativesConversions}
              setTotalRows={(v) => setFilteredTotal(v)}
            />
          )}
        </div>
      </div>
    </AudienceTemplate>
  )
}

export default CreativeReport
